// Player flutuante de rádios para foco. Stack: signals + 1 HTMLAudioElement
// criado quando o usuário toca play. Estação atual e volume persistem em
// localStorage. Estilizado pra encostar no canto inferior direito.
//
// Atalhos (portados do simulador antigo):
//   S            — abrir/fechar player (global, ignora se digitando)
//   Espaço       — play / pause (player aberto)
//   N / →        — próxima estação
//   B / ←        — estação anterior
//   L            — toggle lista de estações
//   +/= / -/_    — volume + / - (passos de 5%)
//   M / 0        — mute on/off
//   Esc          — fecha lista (se aberta) ou o player
//   ↑/↓          — navega lista; Enter toca estação destacada

import { signal, computed } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  Music,
  Music2,
  Play,
  Pause,
  Search,
  SkipBack,
  SkipForward,
  UserRound,
  Volume2,
  VolumeX,
  X,
  List,
  Loader2
} from "lucide-react";
import { AUDIO_STATIONS, type Station } from "./player-stations";

// =====================
// Stations list — vem do DB via /api/student/player-stations.
// Inicializa com a lista hard-coded como fallback durante o carregamento
// e quando offline / falha no fetch. Refetch ao abrir o player garante
// que admin tenha mudanças refletidas em segundos.
// =====================

const stationsList = signal<Station[]>(AUDIO_STATIONS);
let stationsFetched = false;

function getStation(id: string): Station | undefined {
  return stationsList.value.find((s) => s.id === id);
}

function groupByCategory(stations: Station[]): Map<string, Station[]> {
  const out = new Map<string, Station[]>();
  for (const s of stations) {
    const arr = out.get(s.category) ?? [];
    arr.push(s);
    out.set(s.category, arr);
  }
  return out;
}

async function fetchStations() {
  try {
    const res = await fetch("/api/student/player-stations", {
      credentials: "include"
    });
    if (!res.ok) return;
    const data = await res.json();
    if (Array.isArray(data?.stations) && data.stations.length > 0) {
      stationsList.value = data.stations as Station[];
      stationsFetched = true;
    }
  } catch (err) {
    console.warn("[player] fetch stations falhou — usando fallback:", err);
  }
}

// =====================
// Estado persistente
// =====================

const STORAGE_KEY = "music-player-state";
const ENABLED_KEY = "music-player-enabled";
interface PersistedState {
  stationId: string;
  volume: number;
  muted: boolean;
  currentTime?: number;
  paused?: boolean;
}
function readEnabled(): boolean {
  try {
    const raw = localStorage.getItem(ENABLED_KEY);
    if (raw == null) return true;
    return raw === "1";
  } catch {
    return true;
  }
}
function writeEnabled(next: boolean) {
  try {
    localStorage.setItem(ENABLED_KEY, next ? "1" : "0");
  } catch {
    // ignora
  }
}
function loadPersisted(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersistedState>;
      return {
        stationId: parsed.stationId ?? AUDIO_STATIONS[0].id,
        volume: typeof parsed.volume === "number" ? parsed.volume : 60,
        muted: !!parsed.muted,
        currentTime:
          typeof parsed.currentTime === "number" ? parsed.currentTime : 0,
        paused: parsed.paused !== false
      };
    }
  } catch {
    // ignora
  }
  return {
    stationId: AUDIO_STATIONS[0].id,
    volume: 60,
    muted: false,
    currentTime: 0,
    paused: true
  };
}
const initial = loadPersisted();

// =====================
// Signals
// =====================

const open = signal<boolean>(false);
const enabled = signal<boolean>(readEnabled());
// Suprime o player temporariamente (ex.: dentro do Gravador). Diferente de
// `enabled` — não persiste em localStorage, só esconde + pausa enquanto a
// rota/view exige. Quando some, player volta como estava.
const suppressed = signal<boolean>(false);
// Exposto pra outros componentes (Study/Prova) checarem antes de capturar
// atalhos de teclado conflitantes (setas, M, Esc) — quando o player está
// aberto, esses atalhos pertencem ao player.
export const isPlayerOpen = open;
const closing = signal<boolean>(false); // anima shrink-to-origin antes de desmontar
const listOpen = signal<boolean>(false);
const stationsSearch = signal<string>("");

// Fecha com shrink: dispara animação reverse e desmonta no animationend.
function startClose() {
  if (closing.value) return; // já em curso
  closing.value = true;
  listOpen.value = false;
}
function finishClose() {
  closing.value = false;
  open.value = false;
}
export function setMusicPlayerEnabled(next: boolean) {
  enabled.value = next;
  writeEnabled(next);
  if (next) return;
  pauseAudio();
  listOpen.value = false;
  closing.value = false;
  open.value = false;
}

/** Suprime o player temporariamente (ex.: Gravador). Pausa o áudio e
 *  esconde a UI. Não persiste — quando o caller chama com `false` o player
 *  volta ao estado anterior (sem auto-play; usuário toca de novo se quiser). */
export function setMusicPlayerSuppressed(next: boolean) {
  suppressed.value = next;
  if (next) {
    pauseAudio();
    listOpen.value = false;
    closing.value = false;
    open.value = false;
  }
}
const stationId = signal<string>(initial.stationId);
const playing = signal<boolean>(false);
const loading = signal<boolean>(false);
const volume = signal<number>(initial.volume);
const muted = signal<boolean>(initial.muted);
const error = signal<string | null>(null);
const highlightIdx = signal<number | null>(null);

// Now-playing (suportado em estações SomaFM via https://somafm.com/songs/<channel>.json)
interface NowPlaying {
  title: string;
  artist: string;
  albumart?: string;
}
const nowPlaying = signal<NowPlaying | null>(null);
const albumartError = signal<boolean>(false);
let nowPlayingTimer: number | null = null;

function extractSomaChannel(url: string): string | null {
  // ex: https://ice2.somafm.com/fluid-128-mp3 → "fluid"
  //     https://ice5.somafm.com/gsclassic-128-mp3 → "gsclassic"
  const m = url.match(/somafm\.com\/([a-z0-9-]+)-\d+/i);
  return m ? m[1] : null;
}

// Logo oficial da estação SomaFM — usado como fallback quando a albumart
// da faixa não carrega (ou nem existe). 400x400 PNG público em
// `https://somafm.com/img3/<channelid>-400.png`. Para estações não-SomaFM,
// retorna null e o player cai no ícone de Música.
function getStationLogoUrl(station: Station): string | null {
  const ch = extractSomaChannel(station.url);
  return ch ? `https://somafm.com/img3/${ch}-400.png` : null;
}

function normalizeArtUrl(url: string | undefined): string | undefined {
  if (!url || typeof url !== "string") return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  if (trimmed.startsWith("//")) return "https:" + trimmed;
  if (trimmed.startsWith("/")) return "https://somafm.com" + trimmed;
  // Upgrade http→https quando possível (browser bloqueia mixed-content)
  if (trimmed.startsWith("http://")) return "https://" + trimmed.slice(7);
  return trimmed;
}

// Cache de capa por (artist|title). iTunes rate-limita rápido, então
// evitamos repetir lookup pra mesma faixa.
const itunesArtCache = new Map<string, string>();

async function fetchITunesArt(
  artist: string,
  title: string
): Promise<string | null> {
  if (!artist || !title) return null;
  const key = `${artist}|||${title}`.toLowerCase();
  if (itunesArtCache.has(key)) {
    const cached = itunesArtCache.get(key);
    return cached || null;
  }
  try {
    const term = encodeURIComponent(`${artist} ${title}`);
    const res = await fetch(
      `https://itunes.apple.com/search?term=${term}&entity=song&limit=1`,
      { cache: "force-cache" }
    );
    if (!res.ok) {
      itunesArtCache.set(key, "");
      return null;
    }
    const json = await res.json();
    const hit = Array.isArray(json?.results) ? json.results[0] : null;
    // artworkUrl100 → up-scale pra 300x300 (padrão do legado).
    const raw = hit?.artworkUrl100 ? String(hit.artworkUrl100) : "";
    const high = raw.replace(/100x100bb\.(jpg|png|jpeg)$/i, "300x300bb.$1");
    itunesArtCache.set(key, high);
    return high || null;
  } catch {
    itunesArtCache.set(key, "");
    return null;
  }
}

async function fetchNowPlaying(stationUrl: string): Promise<NowPlaying | null> {
  const channel = extractSomaChannel(stationUrl);
  if (!channel) return null;
  try {
    const res = await fetch(`https://somafm.com/songs/${channel}.json`, {
      mode: "cors",
      credentials: "omit"
    });
    if (!res.ok) return null;
    const data = await res.json();
    const songs = Array.isArray(data?.songs) ? data.songs : [];
    const first = songs[0];
    if (!first) return null;
    // SomaFM devolve `albumArt` (capital A) mas em 2026 a maioria dos canais
    // retorna string vazia. Quando não temos arte direta, enriquecemos via
    // iTunes Search API (mesma estratégia do legado).
    let art: string | undefined =
      normalizeArtUrl(first.albumArt) ??
      normalizeArtUrl(first.albumart) ??
      normalizeArtUrl(first.art) ??
      normalizeArtUrl(first.image) ??
      normalizeArtUrl(first.cover);
    const title = String(first.title ?? "").trim();
    const artist = String(first.artist ?? "").trim();
    if (!art && artist && title) {
      art = (await fetchITunesArt(artist, title)) ?? undefined;
    }
    return {
      title,
      artist,
      albumart: art ?? undefined
    };
  } catch {
    return null;
  }
}

function startNowPlayingPolling(stationUrl: string) {
  stopNowPlayingPolling();
  nowPlaying.value = null;
  albumartError.value = false;
  // Primeira leitura imediata, depois polling a cada 30s
  const tick = () => {
    fetchNowPlaying(stationUrl).then((np) => {
      // Se a track mudou, reseta error flag pra dar chance da nova art
      if (
        np &&
        (np.title !== nowPlaying.value?.title ||
          np.artist !== nowPlaying.value?.artist)
      ) {
        albumartError.value = false;
      }
      nowPlaying.value = np;
    });
  };
  tick();
  nowPlayingTimer = window.setInterval(tick, 30000);
}

function stopNowPlayingPolling() {
  if (nowPlayingTimer != null) {
    clearInterval(nowPlayingTimer);
    nowPlayingTimer = null;
  }
  nowPlaying.value = null;
  albumartError.value = false;
}

const currentStation = computed<Station>(() => {
  return (
    getStation(stationId.value) ?? stationsList.value[0] ?? AUDIO_STATIONS[0]
  );
});

function persist() {
  const audio =
    typeof window !== "undefined"
      ? window.__persistentPlayerAudio__ ?? window.__studentPlayerAudio__
      : undefined;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        stationId: stationId.value,
        volume: volume.value,
        muted: muted.value,
        currentTime: audio?.currentTime ?? 0,
        paused: audio?.paused ?? true
      })
    );
  } catch {
    // ignora
  }
}

// =====================
// Player core
// =====================

// IMPORTANTE: salvar o audio na window pra sobreviver a HMR. Sem isso, cada
// reload do módulo cria um audio novo e o anterior fica tocando sozinho.
declare global {
  interface Window {
    __persistentPlayerAudio__?: HTMLAudioElement;
    __studentPlayerAudio__?: HTMLAudioElement;
  }
}

function getSharedAudio(): HTMLAudioElement | undefined {
  if (typeof window === "undefined") return undefined;
  return window.__persistentPlayerAudio__ ?? window.__studentPlayerAudio__;
}

function setSharedAudio(audio: HTMLAudioElement) {
  window.__persistentPlayerAudio__ = audio;
  window.__studentPlayerAudio__ = audio;
}

/** Traduz erros do navegador (Audio API + Promise reject de a.play()) pra
 *  mensagens curtas em PT-BR. Nunca vaza `err.message` cru — esses textos
 *  vêm em inglês do browser e confundem o aluno.
 *
 *  Mensagens propositalmente CURTAS — exibidas em barrinha fina dentro do
 *  player, sem deixar a caixa crescer. Marquee anima se passar do container. */
function friendlyAudioError(err: unknown, audio?: HTMLAudioElement): string {
  const code = audio?.error?.code;
  if (code === 1) return "Reprodução interrompida.";
  if (code === 2) return "Sem conexão - tente outra estação.";
  if (code === 3) return "Falha no stream - tente outra estação.";
  if (code === 4) return "Formato não suportado.";

  if (err && typeof err === "object" && "name" in err) {
    const name = String((err as { name: unknown }).name);
    if (name === "NotAllowedError") return "Toque no play novamente.";
    if (name === "NotSupportedError") return "Formato não suportado.";
    if (name === "AbortError") return "Reprodução cancelada.";
  }
  return "Não consegui tocar - tente outra estação.";
}

function getAudio(): HTMLAudioElement {
  if (typeof window === "undefined") {
    throw new Error("Áudio só funciona no navegador.");
  }
  const shared = getSharedAudio();
  if (shared) return shared;
  if (!window.__persistentPlayerAudio__) {
    const a = new Audio();
    a.preload = "none";
    a.addEventListener("playing", () => {
      playing.value = true;
      loading.value = false;
      error.value = null;
    });
    a.addEventListener("pause", () => {
      playing.value = false;
    });
    a.addEventListener("waiting", () => {
      loading.value = true;
    });
    a.addEventListener("error", () => {
      error.value = friendlyAudioError(null, a);
      playing.value = false;
      loading.value = false;
    });
    setSharedAudio(a);
  }
  return window.__persistentPlayerAudio__!;
}

function applyVolume() {
  const a = getSharedAudio();
  if (!a) return;
  a.muted = muted.value;
  a.volume = Math.max(0, Math.min(1, volume.value / 100));
}

async function playStation(s: Station, opts: { resumeTime?: number } = {}) {
  error.value = null;
  const a = getAudio();
  // Sempre pausa + zera src antes de carregar a nova estação. Isso garante que
  // não fica áudio anterior tocando em paralelo (bug visto em HMR e em alguns
  // navegadores que não param o stream imediatamente ao trocar `src`).
  try {
    a.pause();
    a.src = "";
    a.load();
  } catch {
    // ignora
  }
  a.src = s.url;
  a.load();
  applyVolume();
  if (typeof opts.resumeTime === "number" && opts.resumeTime > 0) {
    const resumeAt = opts.resumeTime;
    const onLoadedMetadata = () => {
      try {
        a.currentTime = resumeAt;
      } catch {
        // ignora
      }
      a.removeEventListener("loadedmetadata", onLoadedMetadata);
    };
    a.addEventListener("loadedmetadata", onLoadedMetadata);
  }
  loading.value = true;
  startNowPlayingPolling(s.url);
  try {
    await a.play();
  } catch (err) {
    error.value = friendlyAudioError(err, a);
    loading.value = false;
  }
}

function pauseAudio() {
  getSharedAudio()?.pause();
  stopNowPlayingPolling();
}

function togglePlay() {
  if (playing.value) pauseAudio();
  else playStation(currentStation.value);
}

function selectStation(s: Station) {
  stationId.value = s.id;
  persist();
  playStation(s);
}

function nextStation() {
  const list = stationsList.value;
  if (list.length === 0) return;
  const idx = list.findIndex((s) => s.id === stationId.value);
  selectStation(list[(idx + 1) % list.length]);
}

function prevStation() {
  const list = stationsList.value;
  if (list.length === 0) return;
  const idx = list.findIndex((s) => s.id === stationId.value);
  selectStation(list[(idx - 1 + list.length) % list.length]);
}

function adjustVolume(delta: number) {
  volume.value = Math.max(0, Math.min(100, volume.value + delta));
  if (volume.value > 0 && muted.value) muted.value = false;
  applyVolume();
  persist();
}

function toggleMute() {
  muted.value = !muted.value;
  applyVolume();
  persist();
}

function toggleList() {
  listOpen.value = !listOpen.value;
  highlightIdx.value = null;
}

// =====================
// Atalhos globais — registrados uma vez no module init
// =====================

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    persist();
  });
  window.addEventListener("beforeunload", () => {
    persist();
  });
  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.key === ENABLED_KEY) {
      enabled.value = readEnabled();
      if (!enabled.value) {
        pauseAudio();
        listOpen.value = false;
        closing.value = false;
        open.value = false;
      }
    }
  });
  function isTyping(target: EventTarget | null): boolean {
    if (!target) return false;
    const t = target as HTMLElement;
    if (t.isContentEditable) return true;
    const tag = t.tagName;
    return tag === "INPUT" || tag === "TEXTAREA";
  }

  window.addEventListener("keydown", (e: KeyboardEvent) => {
    if (!enabled.value) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (isTyping(e.target)) return;

    // S — toggle player (global, mesmo fechado)
    if (e.key === "s" || e.key === "S") {
      e.preventDefault();
      open.value = !open.value;
      if (!open.value) listOpen.value = false;
      return;
    }

    // Resto só vale com player aberto
    if (!open.value) return;

    // Esc — fecha lista (se aberta) ou o player
    if (e.key === "Escape") {
      if (listOpen.value) {
        listOpen.value = false;
        highlightIdx.value = null;
      } else {
        startClose();
      }
      e.preventDefault();
      return;
    }

    // Navegação na lista (↑/↓/Enter)
    if (listOpen.value && (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter")) {
      e.preventDefault();
      const list = stationsList.value;
      if (e.key === "Enter") {
        if (highlightIdx.value != null && list[highlightIdx.value]) {
          selectStation(list[highlightIdx.value]);
          listOpen.value = false;
        }
        return;
      }
      const cur =
        highlightIdx.value ??
        list.findIndex((s) => s.id === stationId.value);
      let nx = e.key === "ArrowDown" ? cur + 1 : cur - 1;
      if (nx < 0) nx = list.length - 1;
      if (nx >= list.length) nx = 0;
      highlightIdx.value = nx;
      return;
    }

    // Atalhos normais do player
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      togglePlay();
      return;
    }
    if (e.key === "n" || e.key === "N" || e.key === "ArrowRight") {
      e.preventDefault();
      nextStation();
      return;
    }
    if (e.key === "b" || e.key === "B" || e.key === "ArrowLeft") {
      e.preventDefault();
      prevStation();
      return;
    }
    if (e.key === "l" || e.key === "L") {
      e.preventDefault();
      toggleList();
      return;
    }
    if (e.key === "+" || e.key === "=") {
      e.preventDefault();
      adjustVolume(5);
      return;
    }
    if (e.key === "-" || e.key === "_") {
      e.preventDefault();
      adjustVolume(-5);
      return;
    }
    if (e.key === "m" || e.key === "M" || e.key === "0") {
      e.preventDefault();
      toggleMute();
    }
  });
}

// =====================
// UI
// =====================

export function MusicPlayer() {
  useEffect(() => {
    enabled.value = readEnabled();
    if (!enabled.value) return;
    stationId.value = initial.stationId;
    volume.value = initial.volume;
    muted.value = initial.muted;
    applyVolume();
    if (!initial.paused) {
      void playStation(
        getStation(initial.stationId) ?? AUDIO_STATIONS[0],
        { resumeTime: initial.currentTime }
      );
    }
  }, []);

  if (!enabled.value || suppressed.value) return null;
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Carrega a lista de rádios da API uma vez. Se falhar, o signal já está
  // com o fallback (AUDIO_STATIONS).
  useEffect(() => {
    if (!stationsFetched) void fetchStations();
  }, []);

  // Garante que volume aplica em mudanças do signal
  useEffect(() => {
    applyVolume();
  });

  // Click fora do painel fecha o player.
  // IMPORTANTE: usar `click` (não `mousedown`/`touchstart`). Em mobile, se a
  // gente fecha em mousedown, o painel já entrou em animação encolhendo
  // quando o touchend chega — o browser não sintetiza um click no botão
  // atrás (touchstart e touchend caem em alvos diferentes). Com `click`, o
  // botão atrás recebe SEU click primeiro (durante o bubble), e só depois o
  // listener fecha o player.
  useEffect(() => {
    if (!open.value) return;
    function onClickAway(e: MouseEvent) {
      const panel = panelRef.current;
      if (!panel) return;
      const target = e.target as Node;
      if (panel.contains(target)) return;
      startClose();
    }
    document.addEventListener("click", onClickAway);
    return () => {
      document.removeEventListener("click", onClickAway);
    };
  }, [open.value]);

  if (!open.value) {
    // Morpho: por padrão fica icon-only. No hover, expande horizontalmente
    // revelando "Música" (paused) ou o nome da estação (playing).
    // Padrão consistente com QuickStudyDock e outros morpho buttons.
    const hoverLabel = playing.value
      ? currentStation.value.name
      : "Música";
    return (
      <button
        type="button"
        onClick={() => (open.value = true)}
        aria-label="Abrir player de música"
        title="Player de música (S)"
        class={`group fixed bottom-4 right-4 z-40 inline-flex h-10 items-center justify-center rounded-full border px-2.5 shadow-lg transition-all duration-200 hover:shadow-xl ${
          playing.value
            ? "border-primary/50 bg-primary text-white shadow-primary/40"
            : "border-border bg-card text-foreground hover:border-primary hover:text-primary"
        }`}
      >
        {loading.value ? (
          <Loader2 className="size-4 shrink-0 animate-spin" />
        ) : (
          <Music className="size-4 shrink-0" />
        )}
        <span class="ml-0 max-w-0 overflow-hidden whitespace-nowrap text-xs font-semibold transition-all duration-200 group-hover:ml-1.5 group-hover:max-w-[160px]">
          {hoverLabel}
        </span>
      </button>
    );
  }

  const isClosing = closing.value;
  return (
    <div
      ref={panelRef}
      class="fixed bottom-4 right-4 z-40 flex w-[min(380px,calc(100vw-2rem))] origin-bottom-right flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
      style={{
        animation: isClosing
          ? "player-pop-out 200ms cubic-bezier(0.4, 0, 1, 1) forwards"
          : "player-pop-in 220ms cubic-bezier(0.16, 1, 0.3, 1)"
      }}
      onAnimationEnd={(e: AnimationEvent) => {
        if (e.animationName === "player-pop-out") finishClose();
      }}
    >
      <style>{`
        @keyframes player-pop-in {
          0% { opacity: 0; transform: scale(0.5) translate(20%, 20%); }
          100% { opacity: 1; transform: scale(1) translate(0, 0); }
        }
        @keyframes player-pop-out {
          0% { opacity: 1; transform: scale(1) translate(0, 0); }
          100% { opacity: 0; transform: scale(0.2) translate(40%, 40%); }
        }
        @keyframes player-list-in {
          0% { opacity: 0; max-height: 0; }
          100% { opacity: 1; max-height: 320px; }
        }
        /* Marquee: loop contínuo com paradinha de 1s entre passes. Track tem
           2 cópias do texto (ver <PlayerMarquee/>), animação translada de 0%
           a -50% — quando -50% é alcançado, está exatamente no início da
           segunda cópia (idêntica), criando ilusão de loop infinito. Pausa
           no hover. */
        @keyframes player-marquee-loop {
          0%, 6.25% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .player-marquee { overflow: hidden; white-space: nowrap; min-width: 0; max-width: 100%; text-overflow: ellipsis; }
        .player-marquee-track { display: inline-block; max-width: 100%; }
        .player-marquee.is-overflow { text-overflow: clip; }
        .player-marquee.is-overflow .player-marquee-track { display: inline-flex; gap: 36px; max-width: none; animation: player-marquee-loop 16s linear infinite; }
        .player-marquee:hover .player-marquee-track { animation-play-state: paused; }
        .player-marquee-text { display: inline-block; }
        .player-marquee:not(.is-overflow) .player-marquee-text + .player-marquee-text { display: none; }
      `}</style>

      {/* Header — sempre mostra ESTAÇÃO + categoria (não troca pra música) */}
      <header class="flex items-center gap-2 border-b border-border px-4 py-3">
        <div class="grid size-10 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
          {playing.value && !loading.value ? (
            <span class="flex items-end gap-0.5" aria-hidden>
              <span class="block h-3 w-0.5 animate-pulse bg-primary" />
              <span
                class="block h-4 w-0.5 animate-pulse bg-primary"
                style={{ animationDelay: "150ms" }}
              />
              <span
                class="block h-2.5 w-0.5 animate-pulse bg-primary"
                style={{ animationDelay: "300ms" }}
              />
            </span>
          ) : (
            <Music className="size-4" />
          )}
        </div>
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-semibold">
            {currentStation.value.name}
          </div>
          <div class="truncate text-[11px] uppercase tracking-wider text-primary/80">
            {currentStation.value.category}
          </div>
        </div>
        <button
          type="button"
          onClick={toggleList}
          aria-label="Lista de estações"
          title="Lista de estações (L)"
          class={`grid size-8 place-items-center rounded-md transition-colors ${
            listOpen.value
              ? "bg-primary text-white"
              : "text-muted-foreground hover:bg-accent hover:text-foreground"
          }`}
        >
          <List className="size-4" />
        </button>
        <button
          type="button"
          onClick={startClose}
          aria-label="Fechar player"
          title="Fechar (Esc)"
          class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>

      {/* Card "Tocando agora" — slide-down ao aparecer, slide-up ao sumir
          quando troca pra estação sem metadata. Usa NowPlayingSlide pra
          armazenar o último valor não-nulo e manter renderizado durante o
          slide-up até a transição terminar. */}
      <NowPlayingSlide station={currentStation.value} value={nowPlaying.value} />

      {/* Lista de estações — ACIMA dos controles */}
      <StationsList />

      {/* Erro inline — barrinha fina com altura fixa. Texto longo entra no
          marquee (loop com paradinha) em vez de quebrar linha e crescer a
          caixa. Mesmo pattern do "Tocando agora". */}
      {error.value && (
        <div class="flex h-7 shrink-0 items-center gap-1.5 border-t border-destructive/30 bg-destructive/10 px-4 text-[11px] text-destructive">
          <span aria-hidden class="shrink-0">⚠</span>
          <PlayerMarquee text={error.value} className="flex-1" />
        </div>
      )}

      {/* Controles — sempre embaixo */}
      <div class="flex items-center gap-2 border-t border-border px-4 py-3">
        <button
          type="button"
          onClick={prevStation}
          aria-label="Estação anterior"
          title="Anterior (B / ←)"
          class="grid size-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <SkipBack className="size-4" />
        </button>
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing.value ? "Pausar" : "Tocar"}
          title="Tocar / Pausar (Espaço)"
          class="grid size-11 place-items-center rounded-full bg-brand-gradient text-white shadow-md shadow-primary/40 transition-all hover:brightness-110"
        >
          {/* Loader só quando NÃO está tocando ainda — durante o playback,
              eventos de 'waiting' (rebuffer) podem ligar loading transiente;
              priorizar playing evita o spinner piscando sobre o botão. */}
          {playing.value ? (
            <Pause className="size-5" />
          ) : loading.value ? (
            <Loader2 className="size-5 animate-spin" />
          ) : (
            <Play className="size-5" />
          )}
        </button>
        <button
          type="button"
          onClick={nextStation}
          aria-label="Próxima estação"
          title="Próxima (N / →)"
          class="grid size-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <SkipForward className="size-4" />
        </button>

        <div class="ml-2 flex flex-1 items-center gap-2">
          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted.value ? "Ativar som" : "Mudo"}
            title="Mudo (M / 0)"
            class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            {muted.value || volume.value === 0 ? (
              <VolumeX className="size-4" />
            ) : (
              <Volume2 className="size-4" />
            )}
          </button>
          <input
            type="range"
            min="0"
            max="100"
            value={volume.value}
            onInput={(e) => {
              const v = Number((e.target as HTMLInputElement).value);
              volume.value = v;
              if (v > 0 && muted.value) muted.value = false;
              applyVolume();
              persist();
            }}
            aria-label="Volume"
            title="Volume (+ / -)"
            class="flex-1 accent-primary"
          />
        </div>
      </div>
    </div>
  );
}

// Card "Tocando agora" com animação slide-down/slide-up. Quando `value` é
// truthy abre (max-height + opacity 1); quando vira null fecha, mas mantém
// o último valor renderizado durante o slide-up pra não piscar vazio. Os
// pulsos visuais (estilo `transition`) usam max-height + opacity nas mesmas
// duração/easing pra suavidade.
function NowPlayingSlide({
  station,
  value
}: {
  station: Station;
  value: NowPlaying | null;
}) {
  // Mantém último valor não-nulo pra renderizar durante o fade-out.
  const [shown, setShown] = useState<NowPlaying | null>(value);
  const isOpen = !!value && !!(value.title || value.artist);
  useEffect(() => {
    if (value && (value.title || value.artist)) {
      setShown(value);
    } else {
      // Mantém shown durante o slide-up; limpa depois da transição (240ms).
      const t = setTimeout(() => setShown(null), 260);
      return () => clearTimeout(t);
    }
  }, [value?.title, value?.artist, value?.albumart]);

  return (
    <div
      class="overflow-hidden transition-[max-height,opacity,border-bottom-width] duration-200 ease-out"
      style={{
        maxHeight: isOpen ? "200px" : "0px",
        opacity: isOpen ? 1 : 0,
        borderBottomWidth: isOpen ? "1px" : "0px",
        borderBottomColor: "hsl(var(--border))"
      }}
      aria-hidden={!isOpen}
    >
      {shown && (
        <div class="px-4 py-3">
          <div class="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Tocando agora
          </div>
          <div class="flex items-center gap-3">
            <TrackArt station={station} nowPlaying={shown} />
            <div class="flex min-w-0 flex-1 flex-col gap-1.5 leading-tight">
              {shown.title && (
                <div class="flex items-center gap-1.5">
                  <Music2
                    width="0.95em"
                    height="0.95em"
                    className="shrink-0 text-primary"
                    aria-hidden
                  />
                  <PlayerMarquee
                    text={shown.title}
                    className="text-sm font-semibold"
                  />
                </div>
              )}
              {shown.artist && (
                <div class="flex items-center gap-1.5 text-muted-foreground">
                  <UserRound
                    width="0.9em"
                    height="0.9em"
                    className="shrink-0"
                    aria-hidden
                  />
                  <PlayerMarquee
                    text={shown.artist}
                    className="text-xs"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Marquee dedicado do player — só anima se o texto excede o container.
// Mede UMA cópia (firstSpanRef) contra o wrap; se overflow, o CSS engata o
// estado .is-overflow que (a) revela a segunda cópia, (b) muda o track pra
// inline-flex, (c) liga a animação. Sem overflow, texto fica estático com
// ellipsis natural.
function PlayerMarquee({
  text,
  className
}: {
  text: string;
  className?: string;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const firstSpanRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const wrap = wrapRef.current;
    const first = firstSpanRef.current;
    if (!wrap || !first) return;
    function check() {
      if (!wrap || !first) return;
      // Mede a primeira cópia (texto único) — o track tem 2 e dobraria.
      const overflows = first.scrollWidth > wrap.clientWidth + 1;
      wrap.classList.toggle("is-overflow", overflows);
    }
    check();
    const ro = new ResizeObserver(check);
    ro.observe(wrap);
    ro.observe(first);
    return () => ro.disconnect();
  }, [text]);

  return (
    <div ref={wrapRef} class={`player-marquee min-w-0 ${className ?? ""}`}>
      <span class="player-marquee-track">
        <span ref={firstSpanRef} class="player-marquee-text">
          {text}
        </span>
        <span class="player-marquee-text" aria-hidden>
          {text}
        </span>
      </span>
    </div>
  );
}

// Arte da faixa atual com fallback em cascata:
//   1) nowPlaying.albumart (vindo da API do SomaFM)
//   2) Logo da estação (SomaFM channel img3 PNG)
//   3) Ícone de Música genérico
// Cada fallback é testado on-the-fly: se 1 falhar, troca pra 2; se 2 falhar,
// renderiza o ícone. Estado por imagem (não signal global) pra cada montagem
// recomeçar a cascata.
function TrackArt({
  station,
  nowPlaying
}: {
  station: Station;
  nowPlaying: NowPlaying | null;
}) {
  const trackArt = nowPlaying?.albumart || null;
  const stationLogo = getStationLogoUrl(station);
  // Tier: 0 = trackArt, 1 = stationLogo, 2 = icon. Reseta a cada nova trilha.
  const [tier, setTier] = useState<0 | 1 | 2>(trackArt ? 0 : stationLogo ? 1 : 2);
  // Reset quando a faixa muda
  useEffect(() => {
    setTier(trackArt ? 0 : stationLogo ? 1 : 2);
  }, [trackArt, stationLogo]);
  if (tier === 2 || (tier === 0 && !trackArt) || (tier === 1 && !stationLogo)) {
    return (
      <div class="grid size-14 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
        <Music className="size-5" />
      </div>
    );
  }
  const src = tier === 0 ? trackArt! : stationLogo!;
  return (
    <img
      src={src}
      alt=""
      class="size-14 shrink-0 rounded-md object-cover ring-1 ring-border"
      onError={() => {
        console.warn(`[player] art tier ${tier} falhou:`, src);
        setTier((t) => (t === 0 ? 1 : 2));
      }}
    />
  );
}

function StationsList() {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const currentRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const isOpen = listOpen.value;
  const query = stationsSearch.value;
  const queryNorm = query.trim().toLowerCase();

  // Quando abrir SEM busca, rola até a estação tocando. Com busca ativa,
  // mantém scroll no topo pra o usuário ver os primeiros resultados.
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      const scroller = scrollRef.current;
      if (!scroller) return;
      if (queryNorm) {
        scroller.scrollTop = 0;
        return;
      }
      const el = currentRef.current;
      if (!el) return;
      scroller.scrollTop = el.offsetTop - 8;
      highlightIdx.value = stationsList.value.findIndex(
        (s) => s.id === stationId.value
      );
    }, 220);
    return () => clearTimeout(t);
  }, [isOpen, queryNorm]);

  // Limpa busca ao fechar — próxima abertura começa fresh.
  useEffect(() => {
    if (!isOpen && query) stationsSearch.value = "";
  }, [isOpen]);

  // Filtra estações por nome, gênero ou categoria (busca case-insensitive).
  const filtered = queryNorm
    ? stationsList.value.filter((s) => {
        const hay = `${s.name} ${s.genre} ${s.category}`.toLowerCase();
        return hay.includes(queryNorm);
      })
    : stationsList.value;
  const grouped = groupByCategory(filtered);
  const noResults = queryNorm && filtered.length === 0;

  // Outer: anima max-height pra slide-down. Inner é o scroller — não
  // pode ter max-h próprio que conflite com o outer.
  return (
    <div
      class="overflow-hidden transition-[max-height,opacity,border-top-width] duration-200 ease-out"
      style={{
        maxHeight: isOpen ? "380px" : "0px",
        opacity: isOpen ? 1 : 0,
        borderTopWidth: isOpen ? "1px" : "0px",
        borderTopColor: "hsl(var(--border))"
      }}
      aria-hidden={!isOpen}
    >
      {/* Search field — fixo no topo, fora da área scrollável */}
      <div class="border-b border-border bg-card/40 px-3 py-2">
        <div class="relative">
          <Search
            width="0.85em"
            height="0.85em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onInput={(ev) =>
              (stationsSearch.value = (ev.target as HTMLInputElement).value)
            }
            placeholder="Buscar estação, gênero ou categoria…"
            class="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-7 text-xs outline-none focus:border-primary"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                stationsSearch.value = "";
                searchRef.current?.focus();
              }}
              title="Limpar busca"
              class="absolute right-1.5 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X width="0.8em" height="0.8em" />
            </button>
          )}
        </div>
      </div>

      {/* Scroller — altura fixa pra forçar overflow funcionar */}
      <div
        ref={scrollRef}
        class="overflow-y-auto"
        style={{ maxHeight: "320px" }}
      >
        {noResults && (
          <div class="px-4 py-8 text-center text-xs text-muted-foreground">
            Nenhuma estação para "{query}".
          </div>
        )}
        {!noResults &&
          Array.from(grouped.entries()).map(([cat, items]) => (
            <div key={cat}>
              <div class="sticky top-0 z-10 border-b border-primary/40 bg-brand-gradient px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
                {cat}
              </div>
              {items.map((s) => {
                const isCurrent = s.id === stationId.value;
                const idx = stationsList.value.findIndex((x) => x.id === s.id);
                const isHighlighted = highlightIdx.value === idx;
                return (
                  <button
                    key={s.id}
                    ref={isCurrent ? currentRef : undefined}
                    type="button"
                    onClick={() => {
                      selectStation(s);
                      listOpen.value = false;
                    }}
                    onMouseEnter={() => (highlightIdx.value = idx)}
                    class={`flex w-full items-center gap-2 px-4 py-2 text-left text-xs transition-colors ${
                      isCurrent
                        ? "bg-primary/15 text-primary"
                        : isHighlighted
                          ? "bg-accent text-foreground"
                          : "text-foreground hover:bg-accent"
                    }`}
                  >
                    <div class="min-w-0 flex-1">
                      <div class="truncate font-medium">{s.name}</div>
                      <div class="truncate text-[10px] text-muted-foreground">
                        {s.genre}
                      </div>
                    </div>
                    {isCurrent && playing.value && (
                      <div class="size-2 shrink-0 animate-pulse rounded-full bg-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
      </div>
    </div>
  );
}
